// Б.2.b: Кустарный стимулятор (syn_kustarnyy_stimulyator, action, common).
//
// Цель: один свой юнит на поле (свежевыставленный тоже подходит — picker).
//
// Эффект:
//   - +2 текущей атаки и +2 текущей брони (до конца игры). ХП не меняется.
//   - Юнит получает keyword FRENZY (Неистовство) до конца игры.
//   - На юнит вешается метка-DoT `kustarnyyStimulator`. Тикает 1 урон в Ф1
//     каждого раунда после раунда розыгрыша.
//
// Б.2.b-fix1 (Баг 1): DoT — ПРОБИВАЮЩИЙ урон. Стимулятор сам даёт +2 брони,
// которая в EOT восстанавливается полностью (диздок 6.5). Без пробития 1 урон
// гасится бронёй и штраф никогда не доедает до ХП. Поэтому applyDamage с
// pierce:true — броня игнорируется, всегда -1 ХП. Триггеры на полученный
// урон (Закалённый кибер-псих, Тёмный нетраннер) и Посмертие при смерти
// работают как обычно.
//
// Стимулятор — это БАФФ через Действие, НЕ Мод:
//   - Не учитывается счётчиком Перегрузки.
//   - Не снимается Техно-палачом (он сносит Моды).
//   - Триггер Бишора «получение Модификации» НЕ срабатывает.
//   - Триггер Хирурга «розыгрыш Мода с руки» НЕ срабатывает.
//   - Счётчик прогрессии Синтетикса НЕ тикает (это не Мод с руки).

import { applyDamage } from '../../combat.js';
import { hasKeyword, KW } from '../../keywords.js';
import { onUnitDamaged, onUnitDied } from '../deus/index.js';

export const CARD_ID = 'syn_kustarnyy_stimulyator';

// Пул целей: только свои живые юниты на поле.
export function getTargets(state, playerIdx) {
  return state.players[playerIdx].unitsOnField();
}

// Розыгрыш Действия. targetCard — Card (юнит игрока). Если умер между Ф2 и Ф3 —
// эффект не применяется (стандартное поведение Действий).
export function resolveStimulator(state, playerIdx, targetCard) {
  const p = state.players[playerIdx];
  if (!targetCard || !targetCard.isAlive() || targetCard.ownerId !== p.id || targetCard.type !== 'unit') {
    state.log.add(`💉 ${p.name}: Кустарный стимулятор — цель недоступна`);
    return { ok: false, reason: 'invalid_target' };
  }
  const before = { atk: targetCard.current.atk, armor: targetCard.current.armor };
  // +2/+2/0 до конца игры. По образцу Мода-«Механодендрит» учитываем persistentArmor,
  // чтобы restoreArmor в EOT не сбросил бонус брони.
  targetCard.current.atk   += 2;
  targetCard.current.armor += 2;
  targetCard.persistentArmor    = (targetCard.persistentArmor    || 0) + 2;
  targetCard.persistentMaxArmor = (targetCard.persistentMaxArmor || 0) + 2;
  // Неистовство (FRENZY) — keyword до конца игры.
  if (!hasKeyword(targetCard, KW.FRENZY)) {
    targetCard.keywords.push(KW.FRENZY);
  }
  // В.1.c-1: помечаем FRENZY как persistent (от внешнего Действия), чтобы
  // линейный триггер Расчёта (frenzy на 1-й линии) не снёс этот keyword
  // при уходе с линии.
  if (!Array.isArray(targetCard.persistentKeywords)) targetCard.persistentKeywords = [];
  if (!targetCard.persistentKeywords.includes(KW.FRENZY)) {
    targetCard.persistentKeywords.push(KW.FRENZY);
  }
  // Метка DoT — placedTurn = текущий раунд; в этом ходу DoT НЕ тикает.
  targetCard.kustarnyyStimulator = { placedTurn: state.currentTurn };
  state.log.add(
    `💉 ${p.name}: Кустарный стимулятор → ${targetCard.name_ru}: +2/+2/0, Неистовство, ` +
    `DoT 1/ход (atk ${before.atk}→${targetCard.current.atk}, броня ${before.armor}→${targetCard.current.armor})`
  );
  return { ok: true };
}

// === Тик DoT ===
// Вызывается из phaseDraw в Ф1. Идёт по обоим игрокам и для каждого живого
// юнита с меткой `kustarnyyStimulator` наносит 1 урон, если currentTurn > placedTurn
// (то есть в раунде размещения DoT не тикает).
//
// Урон через applyDamage — стандартный путь, броня поглощает, триггеры на
// «получение урона» (Закалённый кибер-псих, Тёмный нетраннер) срабатывают.
// При смерти — обычный death-event (Посмертие Эксп. батареи и пр.).
export function tickKustarnyyDoT(state) {
  for (let idx = 0; idx < 2; idx++) {
    const p = state.players[idx];
    // Снимок: не итерируем по «живому» массиву, чтобы removeFromField не сдвигал.
    const carriers = [];
    for (let line = 0; line < 2; line++) {
      for (let slot = 0; slot < 4; slot++) {
        const u = p.field[line][slot];
        if (!u) continue;
        if (!u.isAlive()) continue;
        if (!u.kustarnyyStimulator) continue;
        if (u.kustarnyyStimulator.placedTurn >= state.currentTurn) continue;
        carriers.push(u);
      }
    }
    for (const u of carriers) {
      if (!u.isAlive()) continue;
      const before = { hp: u.current.hp, armor: u.current.armor };
      // Б.2.b-fix1 (Баг 1): pierce:true — DoT игнорирует броню, всегда -1 ХП.
      const r = applyDamage(u, 1, { pierce: true });
      const taken = (r.armorAbsorbed || 0) + (r.hpLost || 0);
      state.log.add(
        `💉 Кустарный стимулятор [${p.name}] → ${u.name_ru}: 1 урон ` +
        `(HP ${before.hp}→${u.current.hp}, броня ${before.armor}→${u.current.armor})`
      );
      if (taken > 0) {
        onUnitDamaged(state, { ownerIdx: idx, card: u, dmg: taken });
      }
      if (u.current.hp <= 0) {
        u.alive = false;
        p.removeFromField(u);
        state.log.add(`✗ ${u.name_ru} [${p.name}] погибает (DoT Стимулятора)`);
        onUnitDied(state, {
          ownerIdx: idx, card: u,
          killer: null, killerIdx: null, killerWasCounter: false
        });
      }
    }
  }
}
